import type { TargetCamera } from "@babylonjs/core/Cameras/targetCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ArenaDefinition } from "@arena/shared";

const PITCH_MIN = 0.35;
const PITCH_MAX = 1.2;
const DRAG_YAW_PER_PX = 0.006;
const DRAG_PITCH_PER_PX = 0.004;
/** Manual camera corrections ease back to the automatic camera after this pause. */
const RETURN_DELAY_MS = 2500;
/** Fighters closer than this to the local player widen the framing. */
const CROWD_RADIUS = 14;
const MAX_EXTRA_DISTANCE = 7;
/** Camera shake decay per second (GDD §4.3: brief, and off with reduced motion). */
const SHAKE_DECAY = 3.5;

/**
 * Raised third-person follow camera (GDD §4.2): follows the local fighter,
 * pulls back when other fighters are around, can be dragged, and slowly
 * returns to the automatic framing — but only while the player is not
 * steering, so the "forward" direction never drifts under the thumb.
 */
export class CameraRig {
  overview=false;
  yaw = 0;
  private pitch = 0;
  private distance = 0;
  private defaults: ArenaDefinition["camera"];
  private readonly target = new Vector3();
  private readonly offset = new Vector3();
  private lastDragAt = -Infinity;
  private initialized = false;
  private shakeAmount = 0;
  private readonly reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  private readonly camera: TargetCamera;
  private fixedHeading = false;

  constructor(camera: TargetCamera, arena: ArenaDefinition) {
    this.camera = camera;
    this.defaults = arena.camera;
    this.setArena(arena);
  }

  /** The room's map changed (D49): its camera, and the Inferno's fixed heading. */
  setArena(arena: ArenaDefinition): void {
    this.fixedHeading = arena.theme === 'inferno';
    this.defaults = arena.camera;
    this.yaw = arena.camera.yaw;
    this.pitch = arena.camera.pitch;
    this.distance = arena.camera.distance;
  }

  drag(dxPx: number, dyPx: number): void {
    if(this.fixedHeading)return;
    this.yaw += dxPx * DRAG_YAW_PER_PX;
    this.pitch = clamp(this.pitch + dyPx * DRAG_PITCH_PER_PX, PITCH_MIN, PITCH_MAX);
    this.lastDragAt = performance.now();
  }

  /** Short camera shake, e.g. 0.3 for a Super Slam that involves the local player. */
  shake(amount: number): void {
    if (this.reducedMotion.matches) return;
    this.shakeAmount = Math.max(this.shakeAmount, amount);
  }

  /** Converts a screen-space stick (x right, y up) into a world-space XZ direction. */
  stickToWorld(sx: number, sy: number): { x: number; z: number } {
    const cos = Math.cos(this.yaw);
    const sin = Math.sin(this.yaw);
    return { x: sx * cos + sy * sin, z: -sx * sin + sy * cos };
  }

  update(dt: number, focus: Vector3 | null, others: readonly Vector3[], steering: boolean, wideShot = false): void {
    if(this.overview){
      this.camera.position.set(0,25,-20);
      this.camera.setTarget(new Vector3(0,0,1.8));
      return;
    }
    if (focus) {
      // Follow through the edges too: clamping the target changes the fighter's
      // perspective scale as they approach the near or far side of the arena.
      const wantedX=focus.x;
      const wantedZ=focus.z;
      if (!this.initialized) {
        this.target.set(wantedX, focus.y + 1, wantedZ);
        this.initialized = true;
      }
      const follow = 1 - Math.exp(-dt * 12);
      this.target.x += (wantedX - this.target.x) * follow;
      this.target.y += (focus.y + 1 - this.target.y) * follow;
      this.target.z += (wantedZ - this.target.z) * follow;

      let spread = 0;
      for (const other of others) {
        const d = Vector3.Distance(focus, other);
        if (d < CROWD_RADIUS) spread = Math.max(spread, d);
      }
      const wanted = this.defaults.distance + Math.max(wideShot ? 4 : 0, clamp((spread - 4) * 0.5, 0, MAX_EXTRA_DISTANCE));
      this.distance += (wanted - this.distance) * (1 - Math.exp(-dt * 2.5));
    }

    if (!steering && performance.now() - this.lastDragAt > RETURN_DELAY_MS) {
      const back = 1 - Math.exp(-dt * 1.2);
      this.yaw += shortestAngle(this.yaw, this.defaults.yaw) * back;
      this.pitch += (this.defaults.pitch - this.pitch) * back;
    }

    const horizontal = Math.cos(this.pitch) * this.distance;
    this.offset.set(-Math.sin(this.yaw) * horizontal, Math.sin(this.pitch) * this.distance, -Math.cos(this.yaw) * horizontal);
    this.camera.position.copyFrom(this.target).addInPlace(this.offset);
    this.camera.setTarget(this.target);

    if (this.shakeAmount > 0.001) {
      const a = this.shakeAmount;
      this.camera.position.x += (Math.random() - 0.5) * a;
      this.camera.position.y += (Math.random() - 0.5) * a;
      this.shakeAmount = Math.max(0, a - dt * SHAKE_DECAY * Math.max(a, 0.1));
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function shortestAngle(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
